import { useState } from 'react';
import { Send, Paperclip } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { AttachmentUploader } from './AttachmentUploader';
import { useSendSupportMessage } from '@/hooks/useSendSupportMessage';
import { cn } from '@/lib/utils';

interface TicketReplyInputProps {
  ticketId: string;
  isStaff: boolean;
}

export const TicketReplyInput = ({ ticketId, isStaff }: TicketReplyInputProps) => {
  const [content, setContent] = useState('');
  const [attachments, setAttachments] = useState<string[]>([]);
  const [isInternalNote, setIsInternalNote] = useState(false);
  const [showAttachments, setShowAttachments] = useState(false);

  const sendMessage = useSendSupportMessage();

  const handleSubmit = async () => {
    if (!content.trim() && attachments.length === 0) return;

    await sendMessage.mutateAsync({
      ticketId,
      content: content.trim(),
      attachments,
      isInternalNote: isStaff ? isInternalNote : false,
    });

    // Reset form
    setContent('');
    setAttachments([]);
    setIsInternalNote(false);
    setShowAttachments(false);
  };

  const handleAddAttachment = (filePath: string) => {
    setAttachments(prev => [...prev, filePath]);
  };

  const handleRemoveAttachment = (filePath: string) => {
    setAttachments(prev => prev.filter(p => p !== filePath));
  };

  const canSubmit = (content.trim().length > 0 || attachments.length > 0) && !sendMessage.isPending;

  return (
    <div className="bg-black/40 border border-white/10 rounded-xl p-4 space-y-4">
      <h3 className="text-sm font-medium text-white">
        {isStaff ? 'Responder ao ticket' : 'Adicionar informação'}
      </h3>

      <Textarea
        value={content}
        onChange={(e) => setContent(e.target.value)}
        placeholder={isStaff ? 'Digite sua resposta...' : 'Adicione mais detalhes ao seu ticket...'}
        className="bg-white/5 border-white/10 text-white placeholder:text-white/40 min-h-[100px] resize-none"
        disabled={sendMessage.isPending}
      />

      {/* Attachments Section */}
      {showAttachments && (
        <AttachmentUploader
          attachments={attachments}
          onUploadComplete={handleAddAttachment}
          onRemove={handleRemoveAttachment}
          maxFiles={5}
        />
      )}

      {/* Actions Row */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setShowAttachments(!showAttachments)}
            className={cn(
              'text-white/60 hover:text-white hover:bg-white/10',
              showAttachments && 'bg-white/10 text-white'
            )}
          >
            <Paperclip className="w-4 h-4 mr-1" />
            Anexar
            {attachments.length > 0 && (
              <span className="ml-1 text-xs bg-luxury-pinkBright/20 text-luxury-pinkBright px-1.5 py-0.5 rounded">
                {attachments.length}
              </span>
            )}
          </Button>

          {/* Internal Note Checkbox - Only for Staff */}
          {isStaff && (
            <div className="flex items-center gap-2">
              <Checkbox
                id="internal-note"
                checked={isInternalNote}
                onCheckedChange={(checked) => setIsInternalNote(checked === true)}
                className="border-white/20 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
              />
              <Label 
                htmlFor="internal-note" 
                className="text-xs text-white/60 cursor-pointer"
              >
                Nota interna (apenas equipe)
              </Label>
            </div>
          )}
        </div>

        <Button
          onClick={handleSubmit}
          disabled={!canSubmit}
          className="bg-luxury-pinkBright hover:bg-luxury-pinkBright/90 text-white"
        >
          <Send className="w-4 h-4 mr-2" />
          {sendMessage.isPending ? 'Enviando...' : 'Enviar'}
        </Button>
      </div>
    </div>
  );
};
